import { observable, action, computed, runInAction } from "mobx";
import moment from "moment";
import toastService from "../services/ToastService";

const IS_BIRD_APP = process.env.REACT_APP_ENV === "bird";

export default class SystemAdministratorStore {
  @observable bool_showCreateMaintenanceAgencyModal;

  @observable
  obj_createMaintenanceAgencyModalData = {
    code: "",
    name: "",
    description: "",
    maintenanceAgencyOwners: [],
  };

  @observable
  obj_systemAdministratorData = {
    maintenanceAgencies: [],
  };

  @observable selectedMaintenanceAgencyId = "";

  @observable
  obj_assignMaintenanceAgencyOwnerModalData = {
    mid: "",
    aids: [],
    role: "OWNER",
  };

  @observable arr_allMaintenanceAgencies = [];

  @observable
  bool_showAssignMaintenanceAgencyOwnerToMaintenanceAgencyModal = false;

  @observable bool_maintenanceAgencyPanelExpanded = true;

  @observable obj_selectedUser = {};

  @observable actionsEventsTableData = {
    error: null,
    filter: {},
    metadata: {},
    results: [],
    totalResults: -1,
    type: "",
  };

  @observable bool_showCompleteLogModal = false;

  @observable obj_selectedEventLog = {};

  @observable str_selectedOrchestration = "";
  @observable
  arr_orchestrationData = {
    results: [],
  };
  @observable arr_orchestrationTableData = [];

  @observable arr_modifiedOrchestrationTableData = [];

  @observable str_selectedEntity = "";
  @observable
  arr_entityData = {
    results: [],
  };
  @observable arr_entityTableData = [];

  @observable arr_modifiedEntityTableData = [];

  @observable
  obj_actionsEventsQueryFilter = {
    type: "",
    filter: {
      type: "",
      datetime: {
        from: moment()
          .startOf("day")
          .format("YYYY-MM-DDTHH:mm:ss.SSSZZ")
          .toString(),
        until: moment()
          .endOf("day")
          .format("YYYY-MM-DDTHH:mm:ss.SSSZZ")
          .toString(),
      },
      processingStart: {
        from: moment()
          .startOf("day")
          .format("YYYY-MM-DDTHH:mm:ss.SSSZZ")
          .toString(),
        until: moment()
          .endOf("day")
          .format("YYYY-MM-DDTHH:mm:ss.SSSZZ")
          .toString(),
      },
      sort: [],
    },
    limit: {
      limit: 10,
      offset: 0,
    },
  };

  @observable str_selectedFunction = null;

  @observable frameworks = [];
  @observable selectedFrameworkId = null;

  @observable userStatistics = [];

  @observable userStatisticsYear = [];

  @observable statisticState = {
    from: this.getDateFromMonthsAgo(),
    until: null,
  };

  constructor(http, exporter) {
    this.http = http;
    this.exporter = exporter;
  }

  @action
  activate_SaStore = () => {
    this.func_fetchSystemAdministratorData();
  };

  @action
  func_toggleCreateMaintenanceAgencyModalVisibility = () => {
    this.bool_showCreateMaintenanceAgencyModal =
      !this.bool_showCreateMaintenanceAgencyModal;
    this.func_resetCreateMaintenanceAgencyModalData();
  };

  @action
  func_handleMaintenanceAgencyCodeChange = (newCode) => {
    this.obj_createMaintenanceAgencyModalData = {
      ...this.obj_createMaintenanceAgencyModalData,
      code: newCode,
    };
  };

  @action
  func_handleMaintenanceAgencyNameChange = (newName) => {
    this.obj_createMaintenanceAgencyModalData = {
      ...this.obj_createMaintenanceAgencyModalData,
      name: newName,
    };
  };

  @action
  func_handleMaintenanceAgencyDescriptionChange = (description) => {
    this.obj_createMaintenanceAgencyModalData = {
      ...this.obj_createMaintenanceAgencyModalData,
      description: description,
    };
  };

  @action
  func_handleMaintenanceAgencyOwnerChange = (newOwner) => {
    this.obj_createMaintenanceAgencyModalData = {
      ...this.obj_createMaintenanceAgencyModalData,
      maintenanceAgencyOwners: [newOwner],
    };
  };

  func_constructCreateMaintenanceAgencyJson() {
    const jsonToSend = {
      actionType: "CreateMaintenanceAgency",
      maintenanceAgency: {
        code: this.obj_createMaintenanceAgencyModalData.code,
        name: this.obj_createMaintenanceAgencyModalData.name,
        description: this.obj_createMaintenanceAgencyModalData.description,
      },
      maintenanceAgencyOwners: [
        this.obj_createMaintenanceAgencyModalData.maintenanceAgencyOwners[0],
      ],
    };

    return jsonToSend;
  }

  @action
  func_fetchSystemAdministratorData = () => {
    if (IS_BIRD_APP) return;

    this.http.fetch_system_administrator_data().then(
      action((data) => {
        if (data) {
          this.obj_systemAdministratorData = data;
          this.arr_allMaintenanceAgencies = data.maintenanceAgencies.map(
            (maItem, maIndex) => ({
              label: maItem.maintenanceAgency.name,
              value: maItem.maintenanceAgency.tid,
            })
          );
          if (this.arr_allMaintenanceAgencies.length > 0)
            this.obj_assignMaintenanceAgencyOwnerModalData.mid =
              this.arr_allMaintenanceAgencies[0].value;
        }
      })
    );
  };

  @action
  func_selectMaintenanceAgency = (selectedMaintenanceAgencyId) => {
    this.selectedMaintenanceAgencyId = selectedMaintenanceAgencyId;
  };

  func_createAssignMaintenanceAgencyOwnerToMaintenanceAgencyJson() {
    let jsonToSend = {};

    jsonToSend.actionType = "ReassignMaintenanceAgencyOwnership";
    jsonToSend.mid = this.obj_assignMaintenanceAgencyOwnerModalData.mid;
    jsonToSend.aids = this.obj_assignMaintenanceAgencyOwnerModalData.aids;
    jsonToSend.role = this.obj_assignMaintenanceAgencyOwnerModalData.role;

    return jsonToSend;
  }

  @action
  func_assignMaintenanceAgencyOwnerToMaintenanceAgency = () => {
    const jsonToSend =
      this.func_createAssignMaintenanceAgencyOwnerToMaintenanceAgencyJson();
    this.func_sendRequest(jsonToSend);
  };

  @action
  func_handleMaintenanceAgencyChange = (newMaintenanceAgency) => {
    this.obj_assignMaintenanceAgencyOwnerModalData = {
      ...this.obj_assignMaintenanceAgencyOwnerModalData,
      mid: newMaintenanceAgency.value,
    };
  };

  @action
  func_handleAssignedMaintenanceAgencyOwnerChange = (
    newMaintenanceAgencyOwners
  ) => {
    this.obj_assignMaintenanceAgencyOwnerModalData = {
      ...this.obj_assignMaintenanceAgencyOwnerModalData,
      aids: newMaintenanceAgencyOwners,
    };
  };

  @action
  func_resetAssignMaintenanceAgencyOwnerModalData = () => {
    this.obj_assignMaintenanceAgencyOwnerModalData = {
      mid: "",
      aids: [],
      role: "OWNER",
    };
  };

  @action
  func_toggleAssignMaintenanceAgencyOwnerToMaintenanceAgencyModalVisibility =
    () => {
      this.bool_showAssignMaintenanceAgencyOwnerToMaintenanceAgencyModal =
        !this.bool_showAssignMaintenanceAgencyOwnerToMaintenanceAgencyModal;
      this.func_resetAssignMaintenanceAgencyOwnerModalData();
    };

  @action
  func_createMaintenanceAgency = (callback) => {
    const jsonToSend = this.func_constructCreateMaintenanceAgencyJson();
    this.func_sendRequest(jsonToSend, callback);
  };

  @action
  func_sendRequest = (jsonToSend, callback) => {
    if (IS_BIRD_APP) return;

    runInAction(() => {
      this.http.func_sendGeneralMessage(jsonToSend, (response) => {
        this.o_alert_data = response;
        if (
          response &&
          response.resultType &&
          response.resultType === "SUCCESS" &&
          callback
        ) {
          callback();
        }
      });
    });
  };

  @action
  func_resetCreateMaintenanceAgencyModalData = () => {
    this.obj_createMaintenanceAgencyModalData = {
      code: "",
      name: "",
      description: "",
      maintenanceAgencyOwners: [],
    };
  };

  @action
  func_toggleMaintenanceAgencyPanelVisibility = () => {
    this.bool_maintenanceAgencyPanelExpanded =
      !this.bool_maintenanceAgencyPanelExpanded;
  };

  @action
  func_selectUser = (newSelectedUser) => {
    this.obj_selectedUser = newSelectedUser;
  };

  areQueryFiltersCorrectlySetuped = (type) => {
    if (type === "event") {
      const { from, until } = this.obj_actionsEventsQueryFilter.filter.datetime;
      if (moment(until).isSameOrAfter(moment(from))) {
        return true;
      }
    } else {
      const { from, until } =
        this.obj_actionsEventsQueryFilter.filter.processingStart;
      if (moment(until).isSameOrAfter(moment(from))) {
        return true;
      }
    }
    return false;
  };

  @action
  updateActionsEventsDateFilter = (date, fromOrUntil, type) => {
    const dateInProperFormat = moment(date).format("YYYY-MM-DDTHH:mm:ss.SSSZZ");
    if (fromOrUntil === "from") {
      if (type === "event") {
        this.obj_actionsEventsQueryFilter.filter.datetime.from =
          dateInProperFormat;
      } else {
        this.obj_actionsEventsQueryFilter.filter.processingStart.from =
          dateInProperFormat;
      }
    } else {
      if (type === "event") {
        this.obj_actionsEventsQueryFilter.filter.datetime.until =
          dateInProperFormat;
      } else {
        this.obj_actionsEventsQueryFilter.filter.processingStart.until =
          dateInProperFormat;
      }
    }
  };

  @action
  changeActionsEventsTableSettings = (state, instance) => {
    this.obj_actionsEventsQueryFilter.limit.limit = state.pageSize;
    this.obj_actionsEventsQueryFilter.limit.offset =
      this.obj_actionsEventsQueryFilter.limit.limit * state.page;
    this.obj_actionsEventsQueryFilter.filter.sort = state.sorted.map((sort) => {
      return {
        column: sort.id,
        direction: sort.desc ? "DESC" : "ASC",
      };
    });
  };

  @action
  func_fetchActionsEventsData = (type) => {
    if (IS_BIRD_APP) return;

    if (this.areQueryFiltersCorrectlySetuped(type)) {
      this.http.fetchActionsEventsData(this.obj_actionsEventsQueryFilter).then(
        action((data) => {
          if (data && data.results) {
            this.actionsEventsTableData = data;
          }
        })
      );
    } else {
      toastService.func_notify(
        "Query filters are not correctly setuped. Please amend them!",
        "error"
      );
    }
  };

  @action
  func_propagateSelectedFunction = (selectedFunction) => {
    if (selectedFunction === "actions") {
      this.obj_actionsEventsQueryFilter.type = "Action";
      this.obj_actionsEventsQueryFilter.filter.type = "Action";
      this.resetActionsEventsTableData();
    }
    if (selectedFunction === "events") {
      this.obj_actionsEventsQueryFilter.type = "Event";
      this.obj_actionsEventsQueryFilter.filter.type = "Event";
      this.resetActionsEventsTableData();
    }
    this.str_selectedFunction = selectedFunction;
  };

  @action
  resetActionsEventsTableData = () => {
    this.actionsEventsTableData = {
      error: null,
      filter: {},
      metadata: {},
      results: [],
      totalResults: -1,
      type: "",
    };
  };

  @action
  func_toggleCompleteLogModalVisibility = () => {
    this.bool_showCompleteLogModal = !this.bool_showCompleteLogModal;
  };

  @action
  func_openCompleteLogOfSelectedEvent = (rowItem) => {
    const { row } = rowItem;
    this.obj_selectedEventLog = row.payload;
    this.func_toggleCompleteLogModalVisibility();
  };

  func_collectOrchestrationTableData(orchestration, resultsArray) {
    let tableData = [];
    switch (orchestration) {
      case "Actor":
        tableData = resultsArray.map((resultItem, resultIndex) => {
          return {
            tid: resultItem.tid,
            login: resultItem.login,
            name: resultItem.name,
            type: resultItem.type,
          };
        });
        break;
      case "Branch":
        tableData = resultsArray.map((resultItem, resultIndex) => {
          return {
            tid: resultItem.tid,
            mid: resultItem.mid,
            pid: resultItem.pid,
            name: resultItem.name,
            branchType: resultItem.branchType,
            creationDate: moment(resultItem.creationDate).format("DD.MM.YYYY"),
            status: resultItem.status,
            description: resultItem.description,
            type: resultItem.type,
          };
        });
        break;
      case "Framework":
        tableData = resultsArray.map((resultItem, resultIndex) => {
          return {
            tid: resultItem.tid,
            mid: resultItem.mid,
            name: resultItem.name,
            code: resultItem.code,
            description: resultItem.description,
            frameworkId: resultItem.frameworkId,
            frameworkStatus: resultItem.frameworkStatus,
            maintenanceAgencyId: resultItem.maintenanceAgencyId,
            type: resultItem.type,
          };
        });
        break;
      case "Project":
        tableData = resultsArray.map((resultItem, resultIndex) => {
          return {
            tid: resultItem.tid,
            mid: resultItem.mid,
            name: resultItem.name,
            projectType: resultItem.projectType,
            status: resultItem.status,
            description: resultItem.description,
            creationDate: moment(resultItem.creationDate).format("DD.MM.YYYY"),
            finalizationDate: moment(resultItem.finalizationDate).format(
              "DD.MM.YYYY"
            ),
            framework: resultItem.framework,
            frameworkFrom: resultItem.frameworkFrom,
            type: resultItem.type,
          };
        });
        break;
      case "MaintenanceAgency":
        tableData = resultsArray.map((resultItem, resultIndex) => {
          return {
            tid: resultItem.tid,
            name: resultItem.name,
            code: resultItem.code,
            maintenanceAgencyId: resultItem.maintenanceAgencyId,
            type: resultItem.type,
          };
        });
        break;
      case "PrivilegeBranch":
        tableData = resultsArray.map((resultItem, resultIndex) => {
          return {
            tid: resultItem.tid,
            bid: resultItem.bid,
            aid: resultItem.aid,
            rola: resultItem.rola,
            type: resultItem.type,
          };
        });
        break;
      case "PrivilegeProject":
        tableData = resultsArray.map((resultItem, resultIndex) => {
          return {
            tid: resultItem.tid,
            pid: resultItem.pid,
            aid: resultItem.aid,
            rola: resultItem.rola,
            type: resultItem.type,
          };
        });
        break;
      case "PrivilegeMaintenanceAgency":
        tableData = resultsArray.map((resultItem, resultIndex) => {
          return {
            tid: resultItem.tid,
            mid: resultItem.mid,
            aid: resultItem.aid,
            rola: resultItem.rola,
            type: resultItem.type,
          };
        });
        break;
      case "PrivilegeSystem":
        tableData = resultsArray.map((resultItem, resultIndex) => {
          return {
            tid: resultItem.tid,
            aid: resultItem.aid,
            app: resultItem.app,
            rola: resultItem.rola,
            type: resultItem.type,
          };
        });
        break;

      default:
        return false;
    }

    return tableData;
  }

  func_collectEntityTableData(entity, resultsArray) {
    const tableData = [];
    let actualKey = "";
    let actualValue = "";

    for (let i = 0; i < resultsArray.length; i++) {
      const keys = Object.keys(resultsArray[i]);
      const values = Object.values(resultsArray[i]);
      let item = {};
      for (let j = 0; j < keys.length; j++) {
        actualKey = keys[j].toString();
        if (this.isDateField(actualKey)) {
          actualValue = moment(values[j]).format("DD.MM.YYYY");
        } else {
          actualValue = values[j];
        }
        item[actualKey.toString()] = actualValue;
      }
      tableData.push(item);
    }

    return tableData;
  }

  isDateField = (fieldKey) => {
    switch (fieldKey) {
      case "creationDate":
        return true;
      case "finalizationDate":
        return true;
      case "validFrom":
        return true;
      case "validTo":
        return true;
      case "memberHierarchyValidFrom":
        return true;
      default:
        return false;
    }
  };

  @action
  func_updateOrchestrationTableData = (modifiedTableData) => {
    this.arr_modifiedOrchestrationTableData = modifiedTableData;
  };

  @action
  func_updateEntityTableData = (modifiedTableData) => {
    this.arr_modifiedEntityTableData = modifiedTableData;
  };

  @action
  func_fetchOrchestrationData = (orchestration) => {
    if (IS_BIRD_APP) return;

    this.http.fetch_entityOrOrchestration_data(orchestration).then(
      action((data) => {
        if (data && data.results) {
          this.arr_orchestrationData = data.results;
          this.arr_orchestrationTableData =
            this.func_collectOrchestrationTableData(
              orchestration,
              data.results
            );
        }
      })
    );
  };

  @action
  func_fetchEntityData = (entity) => {
    if (IS_BIRD_APP) return;

    this.http.fetch_entityOrOrchestration_data(entity).then(
      action((data) => {
        if (data && data.results) {
          this.arr_entityData = data.results;
          this.arr_entityTableData = this.func_collectEntityTableData(
            entity,
            data.results
          );
        }
      })
    );
  };

  @action
  func_selectOrchestration = (selectedOrchestration) => {
    this.str_selectedOrchestration = selectedOrchestration;
    this.str_selectedEntity = "";
    this.func_fetchOrchestrationData(selectedOrchestration);
  };

  @action
  func_selectEntity = (selectedEntity) => {
    this.str_selectedEntity = selectedEntity;
    this.str_selectedOrchestration = "";
    this.func_fetchEntityData(selectedEntity);
  };

  func_updateEntityOrOrchestration = (updatedEntityOrOrchestration) => {
    const jsonToSend = {
      actionType: "UpdateObject",
      object: updatedEntityOrOrchestration,
    };

    this.func_sendRequest(jsonToSend);
  };

  generateProductionChangelog = (frameworkId) => {
    if (IS_BIRD_APP) return;

    this.http.generateProductionChangelog(frameworkId);
  };

  exportLatestProductionChangelog = () => {
    this.exporter.exportLatestProductionChangelog();
  };

  @action
  fetchFrameworks = () => {
    if (IS_BIRD_APP) return;

    this.http.service_fetch_frameworks().then((response) => {
      this.frameworks = response.results;
    });
  };

  @action
  selectFramework = (selectedFrameworkId) => {
    this.selectedFrameworkId = selectedFrameworkId;
  };

  isPropValuesEqual = (subject, target, propNames) => {
    return propNames.every(
      (propName) => subject[propName] === target[propName]
    );
  };

  getUniqueItemsByProperties = (items, propNames) => {
    const propNamesArray = Array.from(propNames);

    return items.filter(
      (item, index, array) =>
        index ===
        array.findIndex((foundItem) =>
          this.isPropValuesEqual(foundItem, item, propNamesArray)
        )
    );
  };

  convertMillisToTime = (millis) => {
    let ms = millis % 1000;
    millis = (millis - ms) / 1000;
    let seconds = millis % 60;
    millis = (millis - seconds) / 60;
    let minutes = millis % 60;
    let hours = (millis - minutes) / 60;

    return hours + ":" + minutes + ":" + seconds + "." + ms;
  };

  getDateFromMonthsAgo = (cc = 1) => {
    let date = new Date();
    let month = date.getMonth();
    date.setMonth(date.getMonth() - cc);

    // If still in same month, set date to last day of
    // previous month
    if (date.getMonth() === month) date.setDate(0);
    date.setHours(0, 0, 0, 0);
    return date;
  };

  @action
  fetchUserStatistics = () => {
    if (IS_BIRD_APP) return;

    const params1 = {
      type: "Statistics",
      filter: {
        type: "Statistics",
        statisticType: "LoadSddApplication",
        received: {
          from: this.statisticState.from,
          until: this.statisticState.until,
        },
      },
    };
    this.http.fetchAccessUsers(params1).then((response) => {
      this.userStatistics = response.results;
    });
  };

  @action
  fetchMonthlyUserStatistics = (params) => {
    if (IS_BIRD_APP) return;

    const params1 = {
      type: "Statistics",
      filter: {
        type: "Statistics",
        statisticType: "LoadSddApplication",
        received: {
          from: this.getDateFromMonthsAgo(12),
        },
      },
    };
    this.http.fetchAccessUsers(params1).then((response) => {
      this.userStatisticsYear = response.results;
    });
  };

  @action
  createDataForCountry = (data = []) => {
    const userAccessData = this.filterInputDataForStatistics(
      this.userStatistics,
      "LoadSddApplication"
    );
    const filteredUserAccessData = this.getUniqueItemsByProperties(
      userAccessData,
      ["actorId"]
    );
    const distinctCounts = filteredUserAccessData.reduce(
      (p, c) => this.countAccessStatistics(p, c, "country", "distinct"),
      {}
    );
    const totalCounts = userAccessData.reduce(
      (p, c) => this.countAccessStatistics(p, c, "country", "total"),
      {}
    );

    return Object.keys(totalCounts).map((k) => {
      return {
        name: k,
        distinctCount: distinctCounts[k],
        totalCount: totalCounts[k],
      };
    });
  };

  @action
  createDataForQueryWizardAccess = (data = []) => {
    const coutryData = this.filterInputDataForStatistics(
      this.userStatistics,
      "LoadQueryWizard"
    );
    const counts = coutryData.reduce(
      (p, c) => this.countAccessStatistics(p, c, "received", "total"),
      {}
    );

    return Object.keys(counts).map((k) => {
      return { name: k, count: counts[k] };
    });
  };

  @action
  createDataForCountUserTime = (data = []) => {
    const coutryData = this.filterInputDataForStatistics(
      this.userStatistics,
      "LoadSddApplication"
    );
    const counts = coutryData.reduce(
      (p, c) =>
        this.countAccessStatistics(p, c, "received", "total", "actorTime"),
      {}
    );

    return Object.keys(counts).map((k) => {
      return { name: k, count: counts[k] };
    });
  };

  @action
  createDataForUserAccess = (data = []) => {
    const userAccessData = this.filterInputDataForStatistics(
      this.userStatistics,
      "LoadSddApplication"
    );
    const filteredUserAccessData = this.getUniqueItemsByProperties(
      userAccessData,
      ["actorId", "received"]
    );
    const distinctCounts = filteredUserAccessData.reduce(
      (p, c) => this.countAccessStatistics(p, c, "received", "distinct"),
      {}
    );
    const totalCounts = userAccessData.reduce(
      (p, c) => this.countAccessStatistics(p, c, "received", "total"),
      {}
    );

    return Object.keys(distinctCounts).map((k) => {
      return {
        name: k,
        distinctCount: distinctCounts[k],
        totalCount: totalCounts[k],
      };
    });
  };

  @action
  createDataForAuthorizationSystem = (data = []) => {
    const userAccessData = this.filterInputDataForStatistics(
      this.userStatistics,
      "LoadSddApplication"
    );
    const filteredUserAccessData =
      userAccessData &&
      this.getUniqueItemsByProperties(userAccessData, ["actorId", "received"]);

    const distinctCounts = filteredUserAccessData.reduce((p, c) => {
      let name = c.received;
      if (!p.hasOwnProperty(name)) {
        p[name] = {
          ECB: 0,
          ESCB: 0,
        };
      }
      if (c.authSystem === "ESCB") {
        p[name].ESCB = p[name].ESCB + 1;
      }
      if (c.authSystem === "ECB") {
        p[name].ECB = p[name].ECB + 1;
      }
      return p;
    }, {});

    return Object.keys(distinctCounts).map((k) => {
      return {
        name: k,
        ECB: distinctCounts[k].ECB,
        ESCB: distinctCounts[k].ESCB,
      };
    });
  };

  @action
  createDataForMonthlyUserAccess = () => {
    if (!this.userStatisticsYear) return [];
    const userAccessData = this.userStatisticsYear
      .filter((item) => item.statisticType === "LoadSddApplication")
      .sort((a, b) => new Date(a.received) - new Date(b.received))
      .map((x) => {
        return {
          ...x,
          received: moment(new Date(x.received)).format("MM/YYYY"),
        };
      });

    const filteredUserAccessData = this.getUniqueItemsByProperties(
      userAccessData,
      ["actorId", "received"]
    );
    const distinctCounts = filteredUserAccessData.reduce(
      (p, c) => this.countAccessStatistics(p, c, "received", "distinct"),
      {}
    );

    return Object.keys(distinctCounts)
      .slice(-12)
      .map((k) => {
        return { name: k, distinctCount: distinctCounts[k] };
      });
  };

  countAccessStatistics = (
    p,
    c,
    groupingType,
    propertyType,
    countType = "accessCount"
  ) => {
    let name = c[groupingType];
    if (!p.hasOwnProperty(name)) {
      p[name] = 0;
    }
    if (propertyType === "distinct") {
      p[name] = p[name] + 1;
    } else {
      p[name] = p[name] + c[countType];
    }
    return p;
  };

  filterInputDataForStatistics = (data, statisticType) => {
    if (!data) return [];
    return data
      .filter((item) => item.statisticType === statisticType)
      .sort((a, b) => new Date(a.received) - new Date(b.received))
      .map((x) => {
        return {
          ...x,
          country: x.country ? x.country : "Others",
          received: new Date(x.received).toLocaleDateString(),
        };
      });
  };

  @action
  onUpdateStatisticFilterItemValues = (from, until) => {
    this.statisticState = {
      ...this.statisticState,
      from: from,
      until: until,
    };
  };

  @computed
  get getFilterState() {
    return this.statisticState;
  }

  @computed
  get get_arr_allMaintenanceAgencies() {
    return this.arr_allMaintenanceAgencies;
  }

  @computed
  get get_bool_showCreateMaintenanceAgencyModal() {
    return this.bool_showCreateMaintenanceAgencyModal;
  }

  @computed
  get get_obj_createMaintenanceAgencyModalData() {
    return this.obj_createMaintenanceAgencyModalData;
  }

  @computed
  get get_obj_systemAdministratorData() {
    return this.obj_systemAdministratorData;
  }

  @computed
  get get_bool_showAssignMaintenanceAgencyOwnerToMaintenanceAgencyModal() {
    return this.bool_showAssignMaintenanceAgencyOwnerToMaintenanceAgencyModal;
  }

  @computed
  get get_obj_assignMaintenanceAgencyOwnerModalData() {
    return this.obj_assignMaintenanceAgencyOwnerModalData;
  }

  @computed
  get get_obj_selectedMaintenanceAgency() {
    return this.obj_systemAdministratorData.maintenanceAgencies.filter(
      (maItem) =>
        maItem.maintenanceAgency.tid === this.selectedMaintenanceAgencyId
    )[0];
  }

  @computed
  get get_bool_maintenanceAgencyPanelExpanded() {
    return this.bool_maintenanceAgencyPanelExpanded;
  }

  @computed
  get get_bool_showMaintenanceAgencyInfoPanel() {
    return Object.keys(this.obj_selectedMaintenanceAgency).length > 0;
  }

  @computed
  get get_obj_selectedUser() {
    return this.obj_selectedUser;
  }

  @computed
  get getActionsEventsTableData() {
    return this.actionsEventsTableData;
  }

  @computed
  get getEventLogTablePageCount() {
    if (
      this.actionsEventsTableData.totalResults === undefined ||
      this.actionsEventsTableData.totalResults === -1
    ) {
      return -1;
    }
    return Math.ceil(
      this.actionsEventsTableData.totalResults /
        this.obj_actionsEventsQueryFilter.limit.limit
    );
  }

  @computed
  get getEventLogTableLimit() {
    return this.obj_actionsEventsQueryFilter.limit.limit;
  }

  @computed
  get get_bool_showCompleteLogModal() {
    return this.bool_showCompleteLogModal;
  }

  @computed
  get get_obj_selectedEventLog() {
    return this.obj_selectedEventLog;
  }

  @computed
  get get_str_selectedOrchestration() {
    return this.str_selectedOrchestration;
  }

  @computed
  get get_str_selectedEntity() {
    return this.str_selectedEntity;
  }

  @computed
  get get_arr_orchestrationData() {
    return this.arr_orchestrationData;
  }

  @computed
  get get_arr_entityData() {
    return this.arr_entityData;
  }

  @computed
  get get_arr_orchestrationTableData() {
    return this.arr_orchestrationTableData;
  }

  @computed
  get get_arr_entityTableData() {
    return this.arr_entityTableData;
  }

  @computed
  get get_obj_actionsEventsQueryFilter() {
    return this.obj_actionsEventsQueryFilter;
  }

  @computed
  get get_str_selectedFunction() {
    return this.str_selectedFunction;
  }

  @computed
  get getFrameworks() {
    if (this.frameworks.length > 0) {
      return this.frameworks.map((framework, index) => ({
        label: framework.maintenanceAgencyId + " - " + framework.name,
        value: framework.frameworkId,
      }));
    } else {
      return this.frameworks;
    }
  }

  @computed
  get getUserStatistics() {
    return this.userStatistics;
  }

  @computed
  get getSelectedFrameworkId() {
    return this.selectedFrameworkId;
  }
}
